import { Helmet } from 'react-helmet-async';
import { Bed, Star, MapPin, ArrowUpRight, MessageCircle } from 'lucide-react';
import { ondeFicarWidget } from '@/lib/widgets';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';

/* ═══════════════════════════════════════════
   ONDE FICAR — Widget de Hospedagem v2
   Cards visuais com fotos, notas e CTA
   direto para WhatsApp
   ═══════════════════════════════════════════ */

interface Pousada {
  nome: string;
  vilarejo: string;
  nota: string;
  avaliacoes: number;
  preco: string;
  destaqueKey: string;
  whatsapp: string;
  mensagemKey: string;
  imagem: string;
  tags: string[];
}

const POUSADAS: Pousada[] = [
  {
    nome: 'Pousada Fim Da Trilha',
    vilarejo: 'Encantadas',
    nota: '4.6',
    avaliacoes: 607,
    preco: 'R$ 280–500',
    destaqueKey: 'widget.stay.pousada1.highlight',
    whatsapp: '554134269017',
    mensagemKey: 'widget.stay.pousada1.message',
    imagem: '/images/fim-da-trilha.jpg',
    tags: ['Perto da Gruta', 'Wi-Fi'],
  },
  {
    nome: 'Pousada Canto da Galheta',
    vilarejo: 'Nova Brasília',
    nota: '4.8',
    avaliacoes: 320,
    preco: 'R$ 350–600',
    destaqueKey: 'widget.stay.pousada2.highlight',
    whatsapp: '5541987654321',
    mensagemKey: 'widget.stay.pousada2.message',
    imagem: '/images/canto-da-galheta.jpg',
    tags: ['Vista para o mar', 'Café da manhã'],
  },
];

function getWhatsAppUrl(phone: string, message: string): string {
  const encoded = encodeURIComponent(message + ' (via vemprailhadomel.com.br — widget-onde-ficar)');
  return `https://wa.me/${phone}?text=${encoded}`;
}

export function OndeFicarWidget() {
  const { t } = useTranslation();
  const { trackWhatsapp, trackAffiliate } = useAnalytics();
  const schema = ondeFicarWidget.getSchema(
    'https://www.booking.com/searchresults.html?ss=Ilha+do+Mel%2C+Paran%C3%A1'
  );

  return (
    <div
      className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden"
      {...ondeFicarWidget.getDataAttrs()}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({ '@context': 'https://schema.org', ...schema })}
        </script>
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-400 px-6 py-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Bed className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-display text-lg tracking-wide">{t('widget.stay.title')}</h4>
            <p className="font-body text-xs text-amber-100">{t('widget.stay.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Cards de pousadas */}
        {POUSADAS.map(p => (
          <div
            key={p.nome}
            className="border border-sand-100 rounded-xl overflow-hidden hover:shadow-md transition-shadow bg-sand-50/30"
          >
            {/* Imagem */}
            <div className="h-32 bg-sand-100 relative overflow-hidden">
              {p.imagem ? (
                <img
                  src={p.imagem}
                  alt={p.nome}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : null}
              <div className="absolute top-3 left-3 flex gap-1">
                {p.tags.map(tag => (
                  <span
                    key={tag}
                    className="bg-white/90 backdrop-blur-sm text-warm-dark text-[10px] font-body font-semibold px-2 py-0.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                <span className="font-body text-xs font-bold text-warm-dark">{p.nota}</span>
                <span className="font-body text-[10px] text-warm-gray">({p.avaliacoes})</span>
              </div>
            </div>

            {/* Info */}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h5 className="font-display text-base text-warm-dark">{p.nome}</h5>
                  <div className="flex items-center gap-1 mt-1 text-warm-gray">
                    <MapPin className="w-3 h-3" />
                    <span className="font-body text-xs">{p.vilarejo}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-lg text-coral">{p.preco}</p>
                  <p className="font-body text-[10px] text-warm-gray">/ noite</p>
                </div>
              </div>

              <p className="font-body text-xs text-teal-700 mt-2">✦ {t(p.destaqueKey)}</p>

              <a
                href={getWhatsAppUrl(p.whatsapp, t(p.mensagemKey))}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                trackWhatsapp({ provider: p.nome, source: 'widget-onde-ficar' });
                ondeFicarWidget.trackClick({ pousada: p.nome, vilarejo: p.vilarejo });
              }}
                className="mt-3 flex items-center justify-center gap-2 w-full bg-coral hover:bg-[#c96a52] text-white text-center font-body font-bold text-sm rounded-xl px-4 py-3 transition-colors uppercase tracking-wide"
              >
                <MessageCircle className="w-4 h-4" />
                {t('widget.stay.bookButton')}
              </a>
            </div>
          </div>
        ))}

        {/* Link Booking */}
        <a
          href="https://www.booking.com/searchresults.html?ss=Ilha+do+Mel%2C+Paran%C3%A1%2C+Brasil"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackAffiliate({ provider: 'Booking.com', platform: 'booking', productType: 'hotel' });
            ondeFicarWidget.trackClick({ destino: 'booking_search' });
          }}
          className="flex items-center justify-center gap-2 text-ocean font-body text-sm font-semibold hover:underline py-2"
        >
          <span>{t('widget.stay.viewMore')}</span>
          <ArrowUpRight className="w-4 h-4" />
        </a>

        <p className="font-body text-[11px] text-warm-gray text-center">
          {t('widget.stay.footerNote')}
        </p>
      </div>
    </div>
  );
}
